import { Checkbox, Table } from "antd";
import type { TableProps } from "antd/es/table";
import type { Key } from "antd/es/table/interface";
import type { ColumnType } from "antd/lib/table/interface";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Entity } from "../../../core/types/fe";
import { TableCursorStyle } from "../styles/TableCursorStyle";
import type { ExtendedColumnsType } from "./Table";

export interface SelectableTableProps<T> {
    getColumnsConfig: (t: Function) => ExtendedColumnsType<T>[];
    propertyAsRowKey: keyof T;
    tableData: T[];
    dataSource: T[];
    onDataChanged: Function;
    onRow?: TableProps<T>["onRow"];
    name: string;
}

export type DataItemWithKey<T> = T & { key: T[keyof T] };

export const SelectableTable = <T extends Entity>({
    getColumnsConfig,
    propertyAsRowKey,
    tableData = [],
    dataSource,
    onDataChanged,
    onRow,
    name,
}: SelectableTableProps<T>) => {
    const { t } = useTranslation();
    const columns = useMemo(() => getColumnsConfig(t), [t, getColumnsConfig]);

    const columnConfig = extendColumnConfig(columns);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const onSelectChange = (newSelectedRowKeys: Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        const newValues = dataSource.filter((terminal) => newSelectedRowKeys.some((id) => terminal.id === id));
        if (newValues) {
            onDataChanged(newValues);
        }
    };

    useEffect(() => {
        if (tableData) {
            const idArray: Key[] = tableData.map((terminal) => terminal[propertyAsRowKey] as Key);
            setSelectedRowKeys(idArray);
        }
    }, [propertyAsRowKey, tableData]);

    const allKeys = useMemo(() => dataSource.map((item) => item[propertyAsRowKey] as Key), [dataSource, propertyAsRowKey]);
    const isAllSelected = selectedRowKeys.length === dataSource.length && dataSource.length > 0;
    const isIndeterminate = selectedRowKeys.length > 0 && selectedRowKeys.length < dataSource.length;

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnTitle: (
            <Checkbox
                name="table_select_all"
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(e) => {
                    onSelectChange(e.target.checked ? allKeys : []);
                }}
            />
        ),
    };

    return (
        <>
            <TableCursorStyle />
            <Table<T>
                id={name}
                rowSelection={rowSelection}
                columns={columnConfig}
                dataSource={addKeyProp<T>(dataSource as Readonly<T[]>, propertyAsRowKey)}
                onRow={onRow}
                pagination={false}
                className="aevi-table"
                rowClassName={() => (onRow ? "aevi-table-row--clickable" : "")}
            />
        </>
    );
};

function extendColumnConfig<T>(columns: ExtendedColumnsType<T>[]): ColumnType<T>[] {
    return columns as ColumnType<T>[];
}

// antd table requires to have a key prop in the table's row object, otherwise
// it will log warning in the console
function addKeyProp<T>(array: ReadonlyArray<T>, propertyAsRowKey: keyof T) {
    return array?.map((obj) => {
        return { ...obj, key: obj[propertyAsRowKey] } as DataItemWithKey<T>;
    });
}
